import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft, FolderKanban, Clock, DollarSign, TrendingUp } from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  StatusBadge,
  Badge,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '@/components/ui';
import { ClientFormModal, ProjectFormModal } from '@/components/forms';
import { useClient, useClientStats, useClientProjects } from '@/hooks/useClients';
import { formatCurrency, formatDate } from '@aelita/shared';
import type { Client } from '@aelita/shared';

export const Route = createFileRoute('/client/$clientId')({
  component: ClientDashboard,
});

function ClientDashboard() {
  const { clientId } = Route.useParams();
  const { data: client, isLoading } = useClient(clientId);
  const { data: stats } = useClientStats(clientId);
  const { data: projects } = useClientProjects(clientId);

  // Modal states
  const [isEditClientOpen, setIsEditClientOpen] = useState(false);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);

  if (isLoading) {
    return (
      <>
        <Header title="Loading..." />
        <PageContainer>
          <p className="text-gray-500 dark:text-gray-400">Loading client details...</p>
        </PageContainer>
      </>
    );
  }

  if (!client) {
    return (
      <>
        <Header title="Client Not Found" />
        <PageContainer>
          <p className="text-gray-500 dark:text-gray-400">The client you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/clients" className="text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block">
            Back to Clients
          </Link>
        </PageContainer>
      </>
    );
  }

  const clientAny = client as any;
  const projectList = (projects as any[]) ?? clientAny.projects ?? [];

  // Stats from API
  const totalProjects = stats?.totalProjects ?? projectList.length;
  const totalHours = stats?.totalHours ?? 0;
  const hoursThisMonth = stats?.hoursThisMonth ?? 0;
  const billableHours = stats?.billableHours ?? 0;
  const totalTasks = stats?.totalTasks ?? 0;
  const tasksByStatus = stats?.tasksByStatus ?? {};
  const projectsByStatus = stats?.projectsByStatus ?? {};
  const recentTimeEntries = stats?.recentTimeEntries ?? [];
  const totalBudgetHours = stats?.totalBudgetHours ?? 0;
  const totalBudgetAmount = stats?.totalBudgetAmount ?? 0;

  // Calculate derived stats
  const activeProjects = (projectsByStatus.active ?? 0) + (projectsByStatus.pipeline ?? 0);
  const _completedProjects = projectsByStatus.completed ?? 0;
  const completedTasks = tasksByStatus.done ?? 0;
  const taskCompletionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const budgetUsedPercent =
    totalBudgetHours > 0 ? Math.min(100, (totalHours / totalBudgetHours) * 100) : 0;

  // Contract info
  const contractValue = Number(clientAny.contractValue) || 0;
  const contractStart = clientAny.contractStart ? new Date(clientAny.contractStart) : null;
  const contractEnd = clientAny.contractEnd ? new Date(clientAny.contractEnd) : null;

  return (
    <>
      <Header
        title={clientAny.name}
        subtitle={
          <div className="flex items-center gap-2">
            <span>{clientAny.industry ?? 'Client'}</span>
            <StatusBadge status={clientAny.status} />
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <Link to="/clients">
              <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />}>
                Back
              </Button>
            </Link>
            <Button onClick={() => setIsEditClientOpen(true)}>Edit Client</Button>
          </div>
        }
      />
      <PageContainer>
        {/* Key Metrics Row */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-950 rounded-lg">
                <FolderKanban className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalProjects}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Projects</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-950 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeProjects}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Active Projects</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-950 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalHours.toFixed(0)}h</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Hours</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-950 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{hoursThisMonth.toFixed(0)}h</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">This Month</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-950 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(contractValue)}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Contract Value</p>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="col-span-2 space-y-6">
            {/* Client Info */}
            <Card>
              <CardHeader>
                <h2 className="font-semibold">Client Details</h2>
              </CardHeader>
              <CardBody>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Status</dt>
                    <dd className="mt-1">
                      <StatusBadge status={clientAny.status} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Industry</dt>
                    <dd className="mt-1 font-medium">{clientAny.industry ?? '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Email</dt>
                    <dd className="mt-1">
                      {clientAny.email ? (
                        <a
                          href={`mailto:${clientAny.email}`}
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          {clientAny.email}
                        </a>
                      ) : (
                        '-'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Phone</dt>
                    <dd className="mt-1 font-medium">{clientAny.phone ?? '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Website</dt>
                    <dd className="mt-1">
                      {clientAny.website ? (
                        <a
                          href={clientAny.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          {clientAny.website}
                        </a>
                      ) : (
                        '-'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Address</dt>
                    <dd className="mt-1 font-medium">{clientAny.address ?? '-'}</dd>
                  </div>
                </dl>

                {/* Contract Details */}
                {(contractValue > 0 || contractStart || contractEnd) && (
                  <div className="mt-4 pt-4 border-t">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Contract Information</h3>
                    <dl className="grid grid-cols-3 gap-4">
                      <div>
                        <dt className="text-sm text-gray-500 dark:text-gray-400">Contract Value</dt>
                        <dd className="mt-1 font-medium">{formatCurrency(contractValue)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm text-gray-500 dark:text-gray-400">Start Date</dt>
                        <dd className="mt-1 font-medium">
                          {contractStart ? formatDate(contractStart) : '-'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm text-gray-500 dark:text-gray-400">End Date</dt>
                        <dd className="mt-1 font-medium">
                          {contractEnd ? formatDate(contractEnd) : '-'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Budget Overview */}
            {(totalBudgetHours > 0 || totalBudgetAmount > 0) && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold">Budget Overview</h2>
                </CardHeader>
                <CardBody>
                  <div className="grid grid-cols-2 gap-6">
                    {totalBudgetHours > 0 && (
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-500 dark:text-gray-400">Hours Budget</span>
                          <span className="font-medium">
                            {totalHours.toFixed(0)} / {totalBudgetHours}h
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                          <div
                            className={`h-3 rounded-full ${
                              budgetUsedPercent > 90
                                ? 'bg-red-500'
                                : budgetUsedPercent > 70
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                            }`}
                            style={{ width: `${budgetUsedPercent}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {budgetUsedPercent.toFixed(0)}% used
                        </p>
                      </div>
                    )}
                    {totalBudgetAmount > 0 && (
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-500 dark:text-gray-400">Total Budget</span>
                          <span className="font-medium">{formatCurrency(totalBudgetAmount)}</span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Billable hours: {billableHours.toFixed(0)}h
                        </p>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Projects */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-semibold">Projects ({totalProjects})</h2>
                <Button size="sm" onClick={() => setIsNewProjectOpen(true)}>
                  New Project
                </Button>
              </CardHeader>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Project</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Type</TableHeader>
                    <TableHeader>Tasks</TableHeader>
                    <TableHeader>Hours</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectList.length > 0 ? (
                    projectList.map((project: any) => (
                      <TableRow key={project.id}>
                        <TableCell>
                          <Link
                            to="/project/$projectId"
                            params={{ projectId: project.id }}
                            className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {project.name}
                          </Link>
                          {project.startDate && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Started {formatDate(new Date(project.startDate))}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={project.status} />
                        </TableCell>
                        <TableCell>
                          <Badge variant="gray">{project.type}</Badge>
                        </TableCell>
                        <TableCell>{project._count?.tasks ?? 0}</TableCell>
                        <TableCell>{project._count?.timeEntries ?? 0}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 dark:text-gray-400">
                        No projects yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>

            {/* Recent Activity */}
            {recentTimeEntries.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold">Recent Activity</h2>
                </CardHeader>
                <CardBody className="space-y-3">
                  {recentTimeEntries.slice(0, 5).map((entry: any) => (
                    <div
                      key={entry.id}
                      className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <Avatar
                        src={entry.employee?.avatarUrl}
                        name={`${entry.employee?.firstName ?? ''} ${entry.employee?.lastName ?? ''}`}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {entry.employee?.firstName} {entry.employee?.lastName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {entry.project?.name} {entry.task && `- ${entry.task.title}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{(entry.duration / 60).toFixed(1)}h</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(new Date(entry.startTime))}
                        </p>
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Project Distribution */}
            <Card>
              <CardHeader>
                <h2 className="font-semibold">Project Status</h2>
              </CardHeader>
              <CardBody className="space-y-3">
                {[
                  { label: 'Active', value: projectsByStatus.active ?? 0, color: 'bg-green-500' },
                  {
                    label: 'Pipeline',
                    value: projectsByStatus.pipeline ?? 0,
                    color: 'bg-blue-500',
                  },
                  {
                    label: 'On Hold',
                    value: projectsByStatus.on_hold ?? 0,
                    color: 'bg-yellow-500',
                  },
                  {
                    label: 'Completed',
                    value: projectsByStatus.completed ?? 0,
                    color: 'bg-gray-500',
                  },
                ]
                  .filter((item) => item.value > 0)
                  .map((item) => (
                    <div key={item.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`h-3 w-3 rounded-full ${item.color}`} />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{item.label}</span>
                      </div>
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ))}
                {totalProjects === 0 && <p className="text-gray-500 dark:text-gray-400 text-sm">No projects yet</p>}
              </CardBody>
            </Card>

            {/* Task Overview */}
            {totalTasks > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold">Task Overview</h2>
                </CardHeader>
                <CardBody>
                  <div className="text-center mb-4">
                    <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">{taskCompletionRate}%</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Completion Rate</p>
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: 'Done', value: tasksByStatus.done ?? 0, color: 'bg-green-500' },
                      {
                        label: 'In Progress',
                        value: tasksByStatus.in_progress ?? 0,
                        color: 'bg-blue-500',
                      },
                      {
                        label: 'To Do',
                        value: (tasksByStatus.todo ?? 0) + (tasksByStatus.backlog ?? 0),
                        color: 'bg-gray-400',
                      },
                    ].map((item) => (
                      <div key={item.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600 dark:text-gray-300">{item.label}</span>
                          <span className="font-medium">{item.value}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className={`${item.color} h-2 rounded-full`}
                            style={{
                              width: `${totalTasks > 0 ? (item.value / totalTasks) * 100 : 0}%`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Contacts */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-semibold">Contacts</h2>
                {/* TODO: Add contact modal */}
                <Button size="sm" variant="ghost" disabled>
                  Add
                </Button>
              </CardHeader>
              <CardBody>
                {clientAny.contacts?.length > 0 ? (
                  <div className="space-y-3">
                    {clientAny.contacts.map((contact: any) => (
                      <div key={contact.id}>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {contact.name}
                          {contact.isPrimary && (
                            <Badge variant="primary" className="ml-2 text-xs">
                              Primary
                            </Badge>
                          )}
                        </p>
                        {contact.title && <p className="text-sm text-gray-500 dark:text-gray-400">{contact.title}</p>}
                        {contact.email && (
                          <a
                            href={`mailto:${contact.email}`}
                            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            {contact.email}
                          </a>
                        )}
                        {contact.phone && <p className="text-sm text-gray-600 dark:text-gray-300">{contact.phone}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No contacts added</p>
                )}
              </CardBody>
            </Card>

            {/* Notes */}
            {clientAny.notes && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold">Notes</h2>
                </CardHeader>
                <CardBody>
                  <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap text-sm">{clientAny.notes}</p>
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      </PageContainer>

      {/* Edit Client Modal */}
      <ClientFormModal
        isOpen={isEditClientOpen}
        onClose={() => setIsEditClientOpen(false)}
        client={client as Client}
      />

      {/* New Project Modal - pre-filled with this client */}
      <ProjectFormModal
        isOpen={isNewProjectOpen}
        onClose={() => setIsNewProjectOpen(false)}
        project={null}
        defaultClientId={clientId}
      />
    </>
  );
}
